import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productService } from '../lib/firebaseService';
import { Plus, Search, Filter, Edit2, Loader2, ArrowRight, Trash2 } from 'lucide-react';
import ProductModal from '../components/ProductModal';
import '../styles/components-extended.css';
import { useAuth } from '../context/AuthContext';
import { t } from '../utils/translation';

export default function Products() {
    const { userRole } = useAuth();
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('All');

    const { data: products, isLoading } = useQuery({
        queryKey: ['products'],
        queryFn: productService.getProducts
    });

    const createMutation = useMutation({
        mutationFn: productService.addProduct,
        onSuccess: () => {
            queryClient.invalidateQueries(['products']);
            setIsModalOpen(false);
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => productService.updateProduct(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['products']);
            setIsModalOpen(false);
            setEditingProduct(null);
        }
    });

    const deleteMutation = useMutation({
        mutationFn: productService.deleteProduct,
        onSuccess: () => {
            queryClient.invalidateQueries(['products']);
        }
    });

    const handleCreate = () => {
        setEditingProduct(null);
        setIsModalOpen(true);
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        setIsModalOpen(true);
    };

    const handleSave = async (data) => {
        if (editingProduct) {
            await updateMutation.mutateAsync({ id: editingProduct.id, data });
        } else {
            await createMutation.mutateAsync(data);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('COMMON.CONFIRM'))) {
            await deleteMutation.mutateAsync(id);
        }
    };

    const filteredProducts = products?.filter(p => {
        const matchesSearch = (p.brand + ' ' + p.name).toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'All' || p.category === filterCategory;
        return matchesSearch && matchesCategory;
    }) || [];

    if (isLoading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-purple-600" size={32} /></div>;

    return (
        <div className="max-w-[1200px] mx-auto">
            {/* Centered container with max-width for "Card" feel on large screens, similar to dashboard */}

            <div className="page-header">
                <div>
                    <h1 className="page-title">{t('PRODUCTS.TITLE')}</h1>
                    <p className="page-subtitle">{t('PRODUCTS.SUBTITLE')}</p>
                </div>
                <button onClick={handleCreate} className="btn btn-primary h-10 px-6 shadow-sm hover:shadow-md">
                    <Plus size={18} className="mr-2" />
                    {t('PRODUCTS.ADD_PRODUCT')}
                </button>
            </div>

            <div className="content-card mb-8">
                {!isLoading && products?.length === 0 ? (
                    /* Zero State - Clean & Centered */
                    <div
                        onClick={handleCreate}
                        className="flex flex-col items-center justify-center py-24 text-center cursor-pointer hover:bg-gray-50 transition-colors rounded-xl border-dashed border-2 border-transparent hover:border-purple-100"
                    >
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('PRODUCTS.EMPTY.TITLE')}</h2>
                        <p className="text-gray-500 max-w-md mx-auto mb-8 text-base">
                            {t('PRODUCTS.EMPTY.DESC')}
                        </p>
                    </div>
                ) : (
                    /* Data View */
                    <>
                        {/* Toolbar */}
                        <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between">
                            <div className="search-bar flex-1">
                                <Search className="search-icon" size={18} />
                                <input
                                    type="text"
                                    placeholder={t('PRODUCTS.SEARCH_PLACEHOLDER')}
                                    className="input search-input"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="w-full md:w-48">
                                <select
                                    className="input cursor-pointer"
                                    value={filterCategory}
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                >
                                    <option value="All">{t('PRODUCTS.FILTER_ALL')}</option>
                                    <option value="Alcohol">Alcool</option>
                                    <option value="Soft">Softs</option>
                                    <option value="Food">Nourriture</option>
                                    <option value="Other">Autre</option>
                                </select>
                            </div>
                        </div>

                        {/* Table */}
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>{t('PRODUCTS.TABLE.PRODUCT')}</th>
                                        <th className="hidden-mobile">{t('PRODUCTS.TABLE.CATEGORY')}</th>
                                        <th className="hidden-mobile">{t('PRODUCTS.TABLE.FORMAT')}</th>
                                        <th>{t('PRODUCTS.TABLE.COST')} / {t('PRODUCTS.TABLE.PRICE')}</th>
                                        <th>{t('PRODUCTS.TABLE.STOCK')}</th>
                                        {userRole === 'admin' && <th className="text-right">{t('COMMON.ACTIONS')}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProducts.map(product => (
                                        <tr key={product.id}>
                                            <td>
                                                <div className="font-semibold text-gray-900 truncate max-w-[120px] md:max-w-none">{product.brand}</div>
                                                <div className="text-sm text-gray-500 truncate max-w-[120px] md:max-w-none">{product.name}</div>
                                            </td>
                                            <td className="hidden-mobile">
                                                <span className={`badge ${product.category === 'Alcohol' ? 'badge-purple' : 'badge-gray'}`}>
                                                    {product.category}
                                                </span>
                                            </td>
                                            <td className="text-gray-600 hidden-mobile">
                                                {product.unitType}
                                                {product.volumeAmount > 0 && (
                                                    <span className="text-xs text-gray-400 block">
                                                        {product.volumeAmount}{product.volumeUnit}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="text-gray-900">€{product.price.toFixed(2)}</div>
                                                <div className="text-xs text-gray-400">Coût: €{product.cost.toFixed(2)}</div>
                                            </td>
                                            <td>
                                                <div className={`font-bold ${product.stock < (product.lowStockThreshold || 10) ? 'text-red-600' : 'text-gray-900'}`}>
                                                    {product.stock}
                                                </div>
                                            </td>
                                            {(userRole === 'admin' || userRole === 'super_admin') && (
                                                <td className="text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <button
                                                            onClick={() => handleEdit(product)}
                                                            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                                                        >
                                                            <Edit2 size={16} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(product.id)}
                                                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Empty State: Search Results */}
                            {!isLoading && filteredProducts.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-16 text-center">
                                    <div className="bg-gray-50 p-4 rounded-full mb-4">
                                        <Search size={32} className="text-gray-400" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900">{t('PRODUCTS.EMPTY.NO_RESULTS')}</h3>
                                    <p className="text-gray-500 max-w-xs mx-auto mt-1 mb-6">
                                        "{searchTerm}"
                                    </p>
                                    <button
                                        onClick={() => { setSearchTerm(''); setFilterCategory('All'); }}
                                        className="btn btn-secondary"
                                    >
                                        {t('PRODUCTS.EMPTY.CLEAR_FILTERS')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <ProductModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                product={editingProduct}
                onSave={handleSave}
            />
        </div >
    );
}
